import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Plus, Building, Users, TrendingUp, DollarSign, Edit, Eye } from 'lucide-react';

// Mock data for demo company
const demoCompanyData = {
  id: 'demo',
  name: 'Stripe',
  tagline: 'The new standard in online payments',
  logo: 'S',
  website: 'https://stripe.com',
  founded: '2010-01-01',
  location: 'San Francisco, CA',
  stage: 'growth',
  metrics: {
    seeking: '$200M',
    growth: '300%',
    users: '2M+',
    arr: '$500M'
  },
  teamMembers: [
    {
      name: 'Patrick Collison',
      role: 'CEO & Co-founder',
      bio: 'Irish entrepreneur, founded Stripe at age 19, previously founded Auctomatic',
      initials: 'PC',
      email: 'patrick@stripe.com'
    },
    {
      name: 'John Collison',
      role: 'President & Co-founder',
      bio: 'Co-founded Stripe with his brother, youngest self-made billionaire',
      initials: 'JC',
      email: 'john@stripe.com'
    }
  ],
  pitchDeck: {
    uploaded: true,
    fileName: 'Stripe_Series_C_Deck.pdf',
    slides: 24,
    lastUpdated: new Date().toISOString()
  },
  financials: {
    year1: '$50M',
    year2: '$150M',
    year3: '$450M',
    year4: '$1.2B',
    year5: '$3.5B',
    burnRate: '$15M',
    cac: '$200',
    ltv: '$8K',
    churn: '2.5%',
    breakeven: 'Already profitable'
  },
  documents: [
    { name: 'Articles of Incorporation', uploaded: true, required: true, fileName: 'articles.pdf', lastUpdated: new Date().toISOString() },
    { name: 'Cap Table & Equity Structure', uploaded: true, required: true, fileName: 'cap_table.pdf', lastUpdated: new Date().toISOString() },
    { name: 'IP Portfolio & Patents', uploaded: true, required: true, fileName: 'ip_portfolio.pdf', lastUpdated: new Date().toISOString() },
    { name: 'Legal Compliance Report', uploaded: true, required: false },
    { name: 'Insurance Documentation', uploaded: true, required: false },
    { name: 'Previous Funding History', uploaded: true, required: false },
    { name: 'Board Meeting Minutes', uploaded: true, required: false },
    { name: 'Customer References', uploaded: true, required: false }
  ],
  productDemo: {
    videoUrl: 'https://example.com/stripe-demo.mp4',
    videoName: 'Stripe_Platform_Demo.mp4',
    lastUpdated: new Date().toISOString(),
    demoLink: 'https://dashboard.stripe.com/demo',
    features: [
      'Accept payments online with 7 lines of code',
      'Support for 135+ currencies and payment methods',
      'Real-time payment processing and webhooks',
      'Advanced fraud detection with Radar',
      'Subscription and recurring billing management'
    ]
  },
  isDemo: true
};
const CompaniesHome = () => {
  const { currentUser } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [handle, setHandle] = useState('');

  useEffect(() => {
    // Set loading to false when we have the selected workspace
    if (selectedWorkspace) {
      setLoading(false);
    }
  }, [selectedWorkspace]);

  // Fetch handle for the selected workspace
  useEffect(() => {
    const fetchHandle = async () => {
      if (!selectedWorkspace || selectedWorkspace.isDemo) {
        if (selectedWorkspace?.isDemo) {
          setHandle('demo-preview');
        }
        return;
      }

      try {
        const handlesRef = collection(db, 'handles');
        const q = query(handlesRef, where('companyId', '==', selectedWorkspace.id));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // Get the first handle (there should only be one per company)
          const handleDoc = querySnapshot.docs[0];
          setHandle(handleDoc.id);
        } else {
          setHandle('');
        }
      } catch (error) {
        console.error('Error fetching handle:', error);
        setHandle('');
      }
    };

    fetchHandle();
  }, [selectedWorkspace]);

  const handleEditPitch = () => {
    if (selectedWorkspace?.isDemo) {
      window.open(`/pitch-builder/demo`, '_blank');
    } else {
      window.open(`/pitch-builder/${selectedWorkspace?.id}`, '_blank');
    }
  };

  const handlePreviewPitch = () => {
    if (selectedWorkspace?.isDemo) {
      window.open(`/demo-preview`, '_blank');
    } else if (handle) {
      window.open(`/p/${handle}`, '_blank');
    } else {
      alert('No handle found. Please create a pitch deck first and set a custom URL.');
    }
  };

  if (loading || !selectedWorkspace) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-white">Loading workspace...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 py-4 sm:py-8 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          {/* {selectedWorkspace.logoUrl && selectedWorkspace.logoUrl.startsWith('http') ? (
            <img
              src={selectedWorkspace.logoUrl}
              alt=""
              className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 object-contain rounded-xl border-2 border-white/30 shadow-lg bg-white/10"
            />
          ) : (
            <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 rounded-xl bg-white/20 border-2 border-white/30 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-lg">
              {selectedWorkspace.logo || selectedWorkspace.name?.[0] || '?'}
            </div>
          )} */}
          <h1 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4 px-1">
            {selectedWorkspace.name} Workspace
          </h1>
          <p className="text-base sm:text-xl text-blue-100 px-1">
            {selectedWorkspace.tagline || 'Manage your pitch materials and fundraising documents'}
          </p>
          {selectedWorkspace.isDemo && (
            <span className="inline-block mt-2 text-sm text-blue-200 bg-blue-800/30 px-4 py-2 rounded-full">
              Demo Workspace
            </span>
          )}
        </div>

        {/* Main Pitch Deck Card */}
        <div className="bg-white/95 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              {selectedWorkspace.logoUrl && selectedWorkspace.logoUrl.startsWith('http') ? (
                <img
                  src={selectedWorkspace.logoUrl}
                  alt=""
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl object-cover flex-shrink-0 border border-gray-200"
                />
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-red-400 to-teal-400 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl font-bold flex-shrink-0">
                  {selectedWorkspace.logo || selectedWorkspace.name?.[0] || '?'}
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{selectedWorkspace.name}</h2>
                <p className="text-sm sm:text-base text-gray-600">{selectedWorkspace.stage || 'Startup Stage'}</p>
              </div>
            </div>
            {selectedWorkspace.isDemo && (
              <span className="px-3 py-1.5 text-xs sm:text-sm font-medium text-blue-600 bg-blue-100 rounded-full self-start sm:self-center">
                Demo
              </span>
            )}
          </div>
          
          <p className="text-gray-700 mb-6 sm:mb-8 text-base sm:text-lg">{selectedWorkspace.tagline}</p>
          
          {/* Metrics */}
          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
              <div className="flex items-center text-gray-700 mb-2">
                <TrendingUp className="h-5 w-5 mr-2 text-green-500" />
                <span className="font-semibold">Growth</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{selectedWorkspace.metrics?.growth || 'N/A'}</p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
              <div className="flex items-center text-gray-700 mb-2">
                <Users className="h-5 w-5 mr-2 text-blue-500" />
                <span className="font-semibold">Users</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{selectedWorkspace.metrics?.users || 'N/A'}</p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
              <div className="flex items-center text-gray-700 mb-2">
                <DollarSign className="h-5 w-5 mr-2 text-purple-500" />
                <span className="font-semibold">Seeking</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{selectedWorkspace.metrics?.seeking || 'N/A'}</p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4">
              <div className="flex items-center text-gray-700 mb-2">
                <Building className="h-5 w-5 mr-2 text-orange-500" />
                <span className="font-semibold">ARR</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{selectedWorkspace.metrics?.arr || 'N/A'}</p>
            </div>
          </div> */}

          {/* Pitch Deck Status */}
          <div className="border-t border-gray-200 pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Pitch Deck Status</h3>
                <p className="text-sm sm:text-base text-gray-600 break-words">
                  {selectedWorkspace.pitchDeck?.uploaded 
                    ? `Uploaded: ${selectedWorkspace.pitchDeck.fileName} (${selectedWorkspace.pitchDeck.slides} slides)`
                    : 'No pitch deck uploaded yet'
                  }
                </p>
                {handle && (
                  <p className="text-xs sm:text-sm text-blue-600 mt-1 break-all">
                    Custom URL: <span className="font-mono">decklink.co/p/{handle}</span>
                  </p>
                )}
                {!handle && !selectedWorkspace.isDemo && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    No custom URL set. Create a pitch deck to set a custom URL.
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={handlePreviewPitch}
                  disabled={!handle}
                  className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                    handle 
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                  title={handle ? `Preview at decklink.co/p/${handle}` : 'No custom URL set'}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
                <button
                  onClick={handleEditPitch}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all text-sm"
                >
                  <Edit className="h-4 w-4" />
                  {selectedWorkspace.pitchDeck?.uploaded ? 'Edit Deck' : 'Create Deck'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompaniesHome; 